'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useMenu as useMenuContext } from '@/providers/menu-provider';
import { useMenu as useMenuUtils } from './use-menu';

/**
 * Hook für dynamische, mehrsprachige Seitentitel
 *
 * Setzt document.title basierend auf:
 * 1. Aktuellem Menüpunkt (wenn vorhanden)
 * 2. Domain-Name (aus xoffi_domain.title oder .name) als Suffix
 *
 * Beispiel: "Bestellungen | Kaie"
 */
export function usePageTitle() {
  const pathname = usePathname();
  const { menuData } = useMenuContext();
  const { getCurrentItem } = useMenuUtils(pathname);
  const [domainName, setDomainName] = useState('XOFFI');

  // Load domain name once on mount
  useEffect(() => {
    const loadDomainName = async () => {
      try {
        // Try cache first
        const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
        const cacheKey = `xoffi_domain_cache_${hostname}`;
        const cached = localStorage.getItem(cacheKey);

        if (cached) {
          const data = JSON.parse(cached);
          if (data.domainInfo?.domain?.title || data.domainInfo?.domain?.name) {
            setDomainName(data.domainInfo.domain.title || data.domainInfo.domain.name);
            return;
          }
        }

        // Fallback: fetch from API
        const response = await fetch('/api/domaincheck', { credentials: 'omit' });
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.domain_data) {
            const name = data.domain_data.title || data.domain_data.name || 'XOFFI';
            setDomainName(name);
          }
        }
      } catch (error) {
        // Silently fail, keep default
      }
    };

    loadDomainName();
  }, []);

  // Update title when pathname or menu changes
  useEffect(() => {
    // Aktuellen Menüpunkt finden
    const currentItem = getCurrentItem(menuData || []);
    const pageTitle = currentItem?.title || currentItem?.menuTitle;

    // Document-Title setzen
    if (pageTitle) {
      document.title = `${pageTitle} | ${domainName}`;
    } else {
      // Fallback: nur Domain-Name
      document.title = domainName;
    }
  }, [pathname, menuData, domainName, getCurrentItem]);
}

export default usePageTitle;
